import { cur } from '../lib/units';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, PenLine, Share2, Trash2 } from 'lucide-react';
import { Pad } from './SignatureBox';
import { staffSheets } from '../lib/timesheet';
import { timesheetPdf } from '../lib/timesheetPdf';
import { download } from '../lib/exporters';

const BOSS = 'giss_ts_boss';

function SigSlot({ t, label, sig, onSign, onClear, testid }) {
  return (
    <div className="tss-pad" data-testid={testid}>
      <b>{label}</b>
      {sig?.data ? <><img src={sig.data} alt="" data-testid={`${testid}-img`} /><button className="btn btn-outline btn-sm" data-testid={`${testid}-clear`} onClick={onClear}><Trash2 size={14} /> {t.sigClear}</button></>
        : <button className="btn btn-dark" data-testid={`${testid}-btn`} onClick={onSign}><PenLine size={16} /> {t.sigSign}</button>}
    </div>
  );
}

// employee + supervisor sign the monthly timesheet with the finger -> PDF via share sheet
export default function TimesheetSign({ t, month, projects, onClose }) {
  const sheets = staffSheets(projects, month, t).filter((x) => x.id !== '_none');
  const [id, setId] = useState(sheets[0]?.id || '');
  const [sig, setSig] = useState({ staff: null, boss: { name: localStorage.getItem(BOSS) || '' } });
  const [pad, setPad] = useState(null);
  const sh = sheets.find((x) => x.id === id);
  const f2 = (v) => v.toLocaleString(t.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const pick = (v) => { setId(v); setSig((s) => ({ staff: null, boss: { name: s.boss.name } })); };
  const send = async () => {
    localStorage.setItem(BOSS, sig.boss.name || '');
    const blob = timesheetPdf(t, month, sh, sig), name = `${t.tzTitle}_${month}_${sh.s.name.replace(/[^\w-]+/g, '_')}.pdf`;
    const file = new File([blob], name, { type: 'application/pdf' });
    if (navigator.canShare?.({ files: [file] })) {
      try { await navigator.share({ files: [file], title: name, text: `${t.tzTitle} ${month} – ${sh.s.name}` }); } catch (e) { if (e?.name !== 'AbortError') toast.error(String(e.message || e)); }
    } else { download(blob, name); toast.message(t.shareFallback, { duration: 7000 }); }
  };
  return createPortal(
    <div className="editor-backdrop" data-testid="timesheet-sign">
      <div className="editor">
        <div className="row pf-head"><h4><PenLine size={18} /> {t.tzSignTitle} {new Date(`${month}-01T12:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</h4><button className="icon-btn" data-testid="timesheet-sign-close" onClick={onClose}><X size={18} /></button></div>
        {!sheets.length ? <p className="muted" data-testid="timesheet-sign-empty">{t.tzNone}</p> : (
          <>
            <select data-testid="timesheet-sign-staff" value={id} onChange={(e) => pick(e.target.value)}>
              {sheets.map((x) => <option key={x.id} value={x.id}>{x.s.name}{x.s.role ? ` (${x.s.role})` : ''}</option>)}
            </select>
            <p className="wl-sum" data-testid="timesheet-sign-sum">{t.tzDays}: <b>{sh.days}</b> · {t.wlWork}: <b>{f2(sh.work)} h</b> · {t.wlTravel}: <b>{f2(sh.travel)} h</b> · {t.wlSum}: <b>{f2(sh.tot)} h</b>{sh.cost !== '' && <> · <b>{f2(sh.cost)} {cur()}</b></>}</p>
            <label className="tss-boss">{t.tzBossName}<input data-testid="timesheet-sign-boss-name" placeholder={t.tzBossName} value={sig.boss.name} onChange={(e) => setSig({ ...sig, boss: { ...sig.boss, name: e.target.value } })} /></label>
            <div className="tss-pads">
              <SigSlot t={t} label={`${t.tzSigStaff}: ${sh.s.name}`} sig={sig.staff} testid="timesheet-sig-staff" onSign={() => setPad('staff')} onClear={() => setSig({ ...sig, staff: null })} />
              <SigSlot t={t} label={t.tzSigBoss} sig={sig.boss} testid="timesheet-sig-boss" onSign={() => setPad('boss')} onClear={() => setSig({ ...sig, boss: { name: sig.boss.name } })} />
            </div>
            <small className="muted">{t.tzSignHint}</small>
            <button className="btn btn-green" data-testid="timesheet-sign-send" disabled={!sig.staff?.data || !sig.boss?.data} onClick={send}><Share2 size={16} /> {t.tzSend}</button>
          </>
        )}
      </div>
      {pad && <Pad t={t} onClose={() => setPad(null)} onDone={(data) => { setSig((s) => ({ ...s, [pad]: { ...(s[pad] || {}), data, at: Date.now() } })); setPad(null); }} />}
    </div>,
    document.body,
  );
}
